import { type PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import config from "../config";
import {
  type AuthSessionChange,
  clearStoredAuthSession,
  setStoredAuthSession,
  subscribeToAuthSession,
} from "../helpers/authSession";
import browserHistory from "../helpers/browserHistory";
import HttpClient from "../helpers/httpclient";

export type AuthCheckState =
  // `background` marks a liveness re-check of a session nothing has reported a
  // change to. It reconciles without hiding the application. A check that is
  // not background may be resolving a different identity, so it must gate.
  | { status: "checking"; generation: number; background: boolean }
  | { status: "valid"; generation: number }
  | { status: "invalid"; generation: number };

function shouldPreserveReturnPath(reason: AuthSessionChange["reason"] | "session-missing"): boolean {
  return reason !== "signout" && reason !== "password-change";
}

export default function AuthenticatedBoundary({ children }: PropsWithChildren) {
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  const generationRef = useRef(0);
  const stateRef = useRef<AuthCheckState>({ generation: 0, status: "invalid" });
  const [state, setState] = useState<AuthCheckState>(stateRef.current);
  // Whether this boundary has ever confirmed a session. Only the first check
  // gates the application; later checks reconcile it in the background.
  const [established, setEstablished] = useState(false);

  const commitState = useCallback((nextState: AuthCheckState) => {
    stateRef.current = nextState;
    setState(nextState);
  }, []);

  const navigateToSignin = useCallback((preserveReturnPath: boolean) => {
    const current = locationRef.current;
    if (preserveReturnPath) localStorage.setItem("_pathname", `${current.pathname}${current.search}`);
    else localStorage.removeItem("_pathname");
    if (current.pathname !== "/signin") browserHistory.push("/signin");
  }, []);

  const invalidate = useCallback(
    (reason: AuthSessionChange["reason"] | "session-missing") => {
      clearStoredAuthSession();
      if (stateRef.current.status !== "invalid") generationRef.current += 1;
      setEstablished(false);
      commitState({ generation: generationRef.current, status: "invalid" });
      navigateToSignin(shouldPreserveReturnPath(reason));
    },
    [commitState, navigateToSignin],
  );

  const validate = useCallback(
    (forceNewGeneration = false, background = false) => {
      const current = stateRef.current;
      if (current.status === "checking" && !forceNewGeneration) return;
      if (current.status === "valid" && !forceNewGeneration) return;

      generationRef.current += 1;
      const generation = generationRef.current;
      commitState({ background, generation, status: "checking" });

      void HttpClient.get(`${config.api}/auth/session`)
        .then((response) => {
          if (generationRef.current !== generation) return;
          const result = response.data || {};
          if (!result.user) {
            invalidate("session-missing");
            return;
          }
          setStoredAuthSession(result.user, result.impersonated ? true : null);
          // App sits outside this route boundary and reads authentication state
          // through the legacy user-update seam. Notify it after bootstrap so
          // an impersonated session restored from the server immediately shows
          // the correct header and end-impersonation controls.
          document.dispatchEvent(new Event("asset:update-user"));
          setEstablished(true);
          commitState({ generation, status: "valid" });
        })
        .catch((error: unknown) => {
          if (generationRef.current !== generation) return;
          console.error(error);
          invalidate("auth-check-failed");
        });
    },
    [commitState, invalidate],
  );

  const reconcileSession = useCallback(
    (change: AuthSessionChange) => {
      if (change.reason === "signout" || change.reason === "cross-tab-session-cleared") {
        invalidate(change.reason);
        return;
      }
      validate(true);
    },
    [invalidate, validate],
  );

  useEffect(() => subscribeToAuthSession(reconcileSession), [reconcileSession]);

  useEffect(() => {
    validate();
  }, [validate]);

  useEffect(() => {
    // Nothing has reported a session change here: focus fires on every tab and
    // application switch, and on dismissing a native file picker. Reconcile in
    // the background so in-progress work such as a partially completed
    // agreement upload survives.
    const handleFocus = () => validate(true, true);
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [validate]);

  const reconcilingEstablishedSession = state.status === "checking" && state.background && established;
  return state.status === "valid" || reconcilingEstablishedSession ? children : null;
}
